import React from 'react';

// Dev-only guard that warns when a focused text input is removed from the DOM
// shortly after typing. This typically indicates a remount caused by an
// unstable React key or a parent re-render pattern that replaces the node.
export default function FocusRemountGuard() {
  React.useEffect(() => {
    // Only run in development
    const meta = import.meta as ImportMeta;
    if (!meta.env?.DEV) return;

    let lastFocused: Element | null = null;
    let lastTypeAt = 0;

    const isTextEntry = (el: Element | null) => {
      if (!el || !(el as HTMLElement).tagName) return false;
      const t = (el as HTMLElement).tagName;
      if (t === 'TEXTAREA') return true;
      if (t === 'INPUT') {
        const type = ((el as HTMLInputElement).type || 'text').toLowerCase();
        return ['text','search','email','number','password','tel','url','time','date'].includes(type);
      }
      return (el as HTMLElement).isContentEditable === true;
    };

    const onFocusIn = (e: FocusEvent) => {
      const el = e.target as Element;
      if (isTextEntry(el)) lastFocused = el;
    };
    const onInput = (e: Event) => {
      const el = e.target as Element;
      if (isTextEntry(el)) {
        lastFocused = el;
        lastTypeAt = Date.now();
      }
    };

    const observer = new MutationObserver(() => {
      if (!lastFocused) return;
      // If the last focused input was removed shortly after typing, warn.
      if (!lastFocused.isConnected && Date.now() - lastTypeAt < 1000) {
        // Compose a helpful warning with a hint about keys.
        // eslint-disable-next-line no-console
        console.warn(
          '[FocusRemountGuard] A focused text field was removed from the DOM within 1s of typing.\n' +
          'This commonly happens when a component is remounted due to a changing React key,\n' +
          'or a parent re-renders with a different subtree. Ensure keys are stable and not\n' +
          'derived from mutable values (e.g., value, JSON.stringify(obj), Math.random()).'
        );
        // Reset so we don\'t spam the console on subsequent mutations.
        lastFocused = null;
      }
    });

    document.addEventListener('focusin', onFocusIn, true);
    document.addEventListener('input', onInput, true);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      document.removeEventListener('focusin', onFocusIn, true);
      document.removeEventListener('input', onInput, true);
      observer.disconnect();
    };
  }, []);

  return null;
}
